const STATUS_META = {
  present: { label: "Present", shortLabel: "P", tone: "success" },
  absent: { label: "Absent", shortLabel: "A", tone: "danger" },
  late: { label: "Late", shortLabel: "L", tone: "warning" },
  weekly_off: { label: "Weekly Off", shortLabel: "WO", tone: "muted" },
  on_leave: { label: "On Leave", shortLabel: "LV", tone: "info" },
  holiday: { label: "Holiday", shortLabel: "H", tone: "violet" },
  "": { label: "", shortLabel: "", tone: "default" },
};

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function getCurrentMonthKey() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function getCurrentDateKey() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

export function shiftMonthKey(monthKey, step) {
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  const date = new Date(year, month - 1 + Number(step || 0), 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function formatMonthHeading(monthKey) {
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

export function formatAttendanceStatus(status) {
  return STATUS_META[status]?.label || "—";
}

export function getAttendanceStatusMeta(status) {
  return STATUS_META[status] || STATUS_META[""];
}

export function createAttendanceDonutStyle(metrics) {
  const present = Number(metrics?.presentToday || 0);
  const absent = Number(metrics?.absentToday || 0);
  const late = Number(metrics?.lateToday || 0);
  const onLeave = Number(metrics?.onLeaveToday || 0);
  const total = Math.max(1, Number(metrics?.totalEmployees || 0));

  const values = [
    ["#6acb7c", (present / total) * 100],
    ["#ff6d6d", (absent / total) * 100],
    ["#ffbf47", (late / total) * 100],
    ["#4d8cf7", (onLeave / total) * 100],
  ];

  let cursor = 0;
  const stops = values
    .filter(([, size]) => size > 0)
    .map(([color, size]) => {
      const nextStop = `${color} ${cursor}% ${cursor + size}%`;
      cursor += size;
      return nextStop;
    });

  return `conic-gradient(${stops.join(", ") || "#edf0f5 0% 100%"})`;
}

export function buildAdminAttendanceCalendar(monthKey, days) {
  const dayMap = new Map((days || []).map((entry) => [entry.dateKey, entry]));
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  const firstDate = new Date(year, month - 1, 1);
  const lastDate = new Date(year, month, 0);
  const leadingDays = firstDate.getDay();
  const totalDays = lastDate.getDate();

  const cells = [];

  for (let index = 0; index < leadingDays; index += 1) {
    cells.push({
      key: `empty-${index}`,
      isPlaceholder: true,
    });
  }

  for (let day = 1; day <= totalDays; day += 1) {
    const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const entry = dayMap.get(dateKey);

    cells.push({
      key: dateKey,
      isPlaceholder: false,
      dateKey,
      day,
      counts: entry?.counts || {
        present: 0,
        absent: 0,
        late: 0,
        weeklyOff: 0,
        onLeave: 0,
        holiday: 0,
      },
    });
  }

  while (cells.length % 7 !== 0) {
    cells.push({
      key: `trailing-${cells.length}`,
      isPlaceholder: true,
    });
  }

  return {
    weekdays: WEEKDAY_LABELS,
    cells,
  };
}

export function parseAttendanceCsv(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(1)
    .map((line) => {
      const parts = line.split(",").map((part) => part.trim().replace(/^"|"$/g, ""));

      return {
        employeeId: parts[0] || "",
        dateKey: parts[1] || "",
        status: parts[2] || "",
        checkInTime: parts[3] || "",
        checkOutTime: parts[4] || "",
        location: parts[5] || "Office",
      };
    })
    .filter((row) => row.employeeId && row.dateKey);
}

export function toTimeInputValue(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
}

export function downloadAttendanceCsv(fileName, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
